<script setup>
import { computed } from 'vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['action']);

// Color parsing helper
const parseColor = color => {
  if (!color) return undefined;
  if (color.length === 9 && color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const a = parseInt(color.slice(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return color;
};

// Size mappings
const sizeMap = {
  xxs: '10px',
  xs: '12px',
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '22px',
  xxl: '28px',
  '3xl': '34px',
  '4xl': '42px',
  '5xl': '52px',
};

const imageSizeMap = {
  xxs: '40px',
  xs: '60px',
  sm: '80px',
  md: '100px',
  lg: '120px',
  xl: '160px',
  xxl: '200px',
  full: '100%',
};

const marginMap = {
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
};

// Component type
const componentType = computed(() => props.item?.type);

// Box styles
const boxStyles = computed(() => {
  const box = props.item;
  const styles = {
    display: 'flex',
    flexDirection: box.layout === 'horizontal' ? 'row' : 'column',
  };

  if (box.spacing) styles.gap = marginMap[box.spacing] || box.spacing;
  if (box.margin) styles.marginTop = marginMap[box.margin] || box.margin;
  if (box.paddingAll) styles.padding = box.paddingAll;
  if (box.paddingTop) styles.paddingTop = box.paddingTop;
  if (box.paddingBottom) styles.paddingBottom = box.paddingBottom;
  if (box.paddingStart) styles.paddingLeft = box.paddingStart;
  if (box.paddingEnd) styles.paddingRight = box.paddingEnd;
  if (box.backgroundColor)
    styles.backgroundColor = parseColor(box.backgroundColor);
  if (box.cornerRadius) styles.borderRadius = box.cornerRadius;
  if (box.borderWidth) {
    styles.borderWidth = box.borderWidth;
    styles.borderStyle = 'solid';
  }
  if (box.borderColor) styles.borderColor = parseColor(box.borderColor);
  if (box.flex !== undefined) styles.flex = box.flex;
  if (box.width) styles.width = box.width;
  if (box.height) styles.height = box.height;
  if (box.justifyContent) styles.justifyContent = box.justifyContent;
  if (box.alignItems) styles.alignItems = box.alignItems;

  // Background gradient
  if (box.background?.type === 'linearGradient') {
    const bg = box.background;
    const startColor = parseColor(bg.startColor) || '#000';
    const endColor = parseColor(bg.endColor) || '#000';
    const angle = bg.angle || '0deg';
    styles.background = `linear-gradient(${angle}, ${startColor}, ${endColor})`;
  }

  return styles;
});

// Text styles
const textStyles = computed(() => {
  const text = props.item;
  const styles = {
    margin: 0,
    lineHeight: 1.4,
  };

  if (text.size) styles.fontSize = sizeMap[text.size] || text.size;
  if (text.color) styles.color = parseColor(text.color);
  if (text.weight === 'bold') styles.fontWeight = '700';
  if (text.align) styles.textAlign = text.align;
  if (text.flex !== undefined) styles.flex = text.flex;
  if (text.margin) styles.marginTop = marginMap[text.margin] || text.margin;
  if (text.wrap) styles.whiteSpace = 'normal';
  if (text.gravity) {
    if (text.gravity === 'center') styles.alignSelf = 'center';
    if (text.gravity === 'bottom') styles.alignSelf = 'flex-end';
  }
  if (text.maxLines) {
    styles.overflow = 'hidden';
    styles.textOverflow = 'ellipsis';
    styles.display = '-webkit-box';
    styles.webkitLineClamp = text.maxLines;
    styles.webkitBoxOrient = 'vertical';
  }

  return styles;
});

// Image styles
const imageStyles = computed(() => {
  const image = props.item;
  const styles = {
    objectFit: image.aspectMode === 'cover' ? 'cover' : 'contain',
    display: 'block',
  };

  if (image.size && image.size !== 'full') {
    styles.width = imageSizeMap[image.size] || image.size;
    styles.height = 'auto';
  } else {
    styles.width = '100%';
  }

  if (image.aspectRatio) {
    styles.aspectRatio = image.aspectRatio.replace(':', '/');
  }

  if (image.flex !== undefined) styles.flex = image.flex;
  if (image.margin) styles.marginTop = marginMap[image.margin] || image.margin;
  if (image.align === 'center') styles.margin = '0 auto';
  if (image.align === 'end') styles.marginLeft = 'auto';
  if (image.gravity === 'center') styles.alignSelf = 'center';

  return styles;
});

// Button styles
const buttonStyles = computed(() => {
  const button = props.item;
  const styles = {
    padding: '10px 16px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center',
    borderRadius: '6px',
    fontWeight: '600',
    fontSize: '14px',
    display: 'block',
    width: '100%',
    textDecoration: 'none',
  };

  if (button.style === 'primary') {
    styles.backgroundColor = button.color || '#00B900';
    styles.color = '#fff';
  } else if (button.style === 'secondary') {
    styles.backgroundColor = 'transparent';
    styles.border = `1px solid ${button.color || '#00B900'}`;
    styles.color = button.color || '#00B900';
  } else {
    styles.backgroundColor = 'transparent';
    styles.color = button.color || '#00B900';
  }

  if (button.margin)
    styles.marginTop = marginMap[button.margin] || button.margin;
  if (button.height === 'sm') styles.padding = '6px 12px';
  if (button.height === 'md') styles.padding = '10px 16px';

  return styles;
});

// Separator styles
const separatorStyles = computed(() => {
  const sep = props.item;
  return {
    height: '1px',
    backgroundColor: parseColor(sep.color) || '#e0e0e0',
    margin: `${marginMap[sep.margin] || '8px'} 0`,
    width: '100%',
  };
});

// Filler styles
const fillerStyles = computed(() => {
  return { flex: props.item.flex || 1 };
});

// Icon styles
const iconStyles = computed(() => {
  const icon = props.item;
  return {
    width: sizeMap[icon.size] || icon.size || '16px',
    height: sizeMap[icon.size] || icon.size || '16px',
  };
});

// Handle action click
const handleAction = action => {
  if (action?.type === 'uri' && action?.uri) {
    window.open(action.uri, '_blank');
  }
  emit('action', action);
};

// Check if item has action
const hasAction = computed(() => !!props.item?.action);
const actionCursor = computed(() => (hasAction.value ? 'pointer' : 'default'));
</script>

<script>
export default {
  name: 'FlexComponent',
};
</script>

<template>
  <!-- Box -->
  <div
    v-if="componentType === 'box'"
    :style="boxStyles"
    :class="{ clickable: hasAction }"
    @click="hasAction && handleAction(item.action)"
  >
    <FlexComponent
      v-for="(child, index) in item.contents"
      :key="index"
      :item="child"
      @action="handleAction"
    />
  </div>

  <!-- Text -->
  <p
    v-else-if="componentType === 'text'"
    :style="textStyles"
    :class="{ clickable: hasAction }"
    @click="hasAction && handleAction(item.action)"
  >
    {{ item.text }}
  </p>

  <!-- Image -->
  <img
    v-else-if="componentType === 'image'"
    :src="item.url"
    :alt="item.alt || 'Image'"
    :style="imageStyles"
    :class="{ clickable: hasAction }"
    @click="hasAction && handleAction(item.action)"
  />

  <!-- Button -->
  <button
    v-else-if="componentType === 'button'"
    :style="buttonStyles"
    @click="handleAction(item.action)"
  >
    {{ item.action?.label || 'Button' }}
  </button>

  <!-- Separator -->
  <div v-else-if="componentType === 'separator'" :style="separatorStyles" />

  <!-- Filler -->
  <div v-else-if="componentType === 'filler'" :style="fillerStyles" />

  <!-- Icon -->
  <img
    v-else-if="componentType === 'icon'"
    :src="item.url"
    :alt="item.alt || 'Icon'"
    :style="iconStyles"
  />

  <!-- Span (inline text) -->
  <span
    v-else-if="componentType === 'span'"
    :style="textStyles"
    :class="{ clickable: hasAction }"
    @click="hasAction && handleAction(item.action)"
  >
    {{ item.text }}
  </span>

  <!-- Fallback for unknown types -->
  <div v-else class="flex-unknown">
    <!-- Unknown type: {{ componentType }} -->
  </div>
</template>

<style scoped>
.clickable {
  cursor: pointer;
}

.clickable:hover {
  opacity: 0.9;
}

.flex-unknown {
  display: none;
}

p {
  word-break: break-word;
}

img {
  max-width: 100%;
}

button {
  transition: opacity 0.2s;
}

button:hover {
  opacity: 0.85;
}
</style>
